<template>
  <div class="conditions-dashboard">
    <div class="conditions-dashboard__loader-container" v-if="loading">
      Loading...
    </div>

    <ConditionsClearedTrends
      v-if="authToken && azureId && !trendsCollapsed"
      :azure-id="azureId"
      :auth-token="authToken"
      :org-id="null"
      :rows="allData"
      @summary="setTrendsSummary"
    />
  </div>
</template>

<script>
import ConditionsClearedTrends from "./ConditionsClearedTrends.vue"
import ApiService from "@/api/api.service.js"
import auth from "@/authConfig"
import { purgeOldPhotoCache } from "@/profilePhotoCache.js"
import { fetchConditionsNeedingAttention } from "@/helpers/conditionsDashboardData"
import SignalRService from "@/api/signalr.service.js"

const LS_TRENDS_COLLAPSED = "conditions_dashboard_trends_collapsed"
const LS_TRENDS_SUMMARY = "conditions_dashboard_trends_summary"

export default {
  name: "ConditionsDashboard",
  components: {
    ConditionsClearedTrends
  },
  data() {
    return {
      trendsCollapsed: false,
      trendsSummary: "",
      azureId: "",
      authToken: "",
      loading: false,
      columnsLoaded: false,
      allData: [],

      signalrStarted: false,
      signalrDebounce: null,
      signalrSyncDebounce: null
    }
  },
  beforeUnmount() {
    clearTimeout(this.signalrDebounce)
    clearTimeout(this.signalrSyncDebounce)
  },
  methods: {
    closePillClass(item) {
      const v = item?.estimatedClosingDate || item?.attentionSetAt || ""
      if (!v) return "pill--gray"

      const d = new Date(v)
      if (isNaN(d.getTime())) return "pill--gray"

      const now = new Date()
      const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24))

      if (diffDays <= 3) return "pill--red"
      if (diffDays <= 7) return "pill--yellow"
      return "pill--green"
    },

    toggleTrends() {
      const next = !this.trendsCollapsed
      this.trendsCollapsed = next
      localStorage.setItem(LS_TRENDS_COLLAPSED, next ? "1" : "0")
    },

    setTrendsSummary(v) {
      this.trendsSummary = String(v || "")
      localStorage.setItem(LS_TRENDS_SUMMARY, this.trendsSummary)
    },

    buildBorrowerName(x) {
      const last = x.borrowerLastName || ""
      const first = x.borrowerFirstName || ""
      if (!last && !first) return ""
      if (last && first) return `${last}, ${first}`
      return last || first
    },

    shortDateTime(v) {
      if (!v) return ""
      const d = new Date(v)
      if (isNaN(d.getTime())) return ""
      const mm = String(d.getMonth() + 1).padStart(2, "0")
      const dd = String(d.getDate()).padStart(2, "0")
      const yyyy = d.getFullYear()
      const hh = d.getHours() % 12 || 12
      const mi = String(d.getMinutes()).padStart(2, "0")
      const ap = d.getHours() >= 12 ? "PM" : "AM"
      return `${mm}/${dd}/${yyyy} ${hh}:${mi} ${ap}`
    },

    normalizeRow(x) {
      const parts = [x.propertyAddress, x.propertyCity, x.propertyState, x.propertyZip].filter(Boolean)
      return {
        ...x,
        attentionSetAt: this.shortDateTime(x.attentionSetAt),
        borrowerName: this.buildBorrowerName(x),
        fullPropertyAddress: parts.join(", ")
      }
    },

    async fetchAttention() {
      this.loading = true
      try {
        this.trendsSummary = localStorage.getItem(LS_TRENDS_SUMMARY) || ""
        const result = await fetchConditionsNeedingAttention()
        this.azureId = result.azureId
        this.authToken = result.authToken
        this.allData = result.allData || []
      } finally {
        this.loading = false
        this.columnsLoaded = true
      }
    },

    scheduleFullResync() {
      clearTimeout(this.signalrSyncDebounce)
      this.signalrSyncDebounce = setTimeout(() => {
        this.fetchAttention()
      }, 800)
    },

    async startSignalR() {
      if (this.signalrStarted) return

      try {
        await SignalRService.startConditionsConnection()

        SignalRService.onConditionsReconnected(() => {
          console.log("SignalR: ConditionsHub reconnected, refreshing dashboard")
          this.scheduleFullResync()
        })

        SignalRService.onNeedsAttentionChanged(message => {
          this.queueRefreshFromSignalR(message)
          this.scheduleFullResync()
        })

        this.signalrStarted = true
        console.log("SignalR: ConditionsDashboard subscribed to NeedsAttentionChanged")
      } catch (err) {
        console.error("SignalR: failed to start ConditionsHub", err)
      }
    },

    queueRefreshFromSignalR(message) {
      const updated = message?.arguments?.[0] || message
      if (!updated?.conditionId) return

      clearTimeout(this.signalrDebounce)
      this.signalrDebounce = setTimeout(() => {
        const src = Array.isArray(this.allData) ? [...this.allData] : []
        const idx = src.findIndex(x => x.conditionId === updated.conditionId)

        const shouldRemove =
          updated.needsAttention === false ||
          updated.isActive === false

        if (shouldRemove) {
          if (idx !== -1) src.splice(idx, 1)
          this.allData = src
          return
        }

        if (idx !== -1) {
          src[idx] = { ...src[idx], ...updated }
        } else {
          src.unshift(updated)
        }

        this.allData = src
      }, 100)
    }
  },
  async mounted() {
    const savedTrends = localStorage.getItem(LS_TRENDS_COLLAPSED)
    if (savedTrends === "1") this.trendsCollapsed = true
    if (savedTrends === "0") this.trendsCollapsed = false

    await purgeOldPhotoCache(1000 * 60 * 60 * 24 * 7, 200)
    await this.fetchAttention()
    await this.startSignalR()
  }
}
</script>

<style scoped>
.conditions-dashboard {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: auto;
  min-width: 1000px;
}

.conditions-dashboard__loader-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  inset: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(2px);
}
</style>
